import { SIZE_ORDER } from "@command-center-app/helpers/data.tsx";
import { IContactInfo } from "@command-center-app/index";

export interface ProductWarning {
  field?: string;
  message: string;
  rule?: string;
}

/**
 * Builds de-duplicated, user-facing "Sync Failure" messages for SF-sync data
 * issues returned by a product update mutation. The product field was still
 * saved; these messages simply tell the user that required data is still
 * missing for Salesforce sync while the product is Active.
 *
 * These are rendered inline in the section below the product tabs (via
 * ErrorAlert), not as toasts, so they persist until the issue is corrected.
 */
export const buildProductWarningMessages = (
  warnings?: ProductWarning[] | null,
): string[] => {
  if (!warnings?.length) return [];

  const seen = new Set<string>();
  return warnings
    .map((w) => w?.message)
    .filter((m): m is string => typeof m === "string" && m.trim().length > 0)
    .filter((m) => (seen.has(m) ? false : (seen.add(m), true)))
    .map((m) => {
      const text = /[.!?]$/.test(m.trim()) ? m.trim() : `${m.trim()}.`;
      return `Sync Failure: ${text} Please correct issue or update the product status`;
    });
};
export const extractApolloErrorMessages = (error: any): string[] => {
  const messages: string[] = [];

  if (error?.graphQLErrors?.length) {
    messages.push(...error.graphQLErrors.map((e: any) => e.message));
  }

  if (
    error?.networkError?.result?.errors?.length &&
    error?.networkError?.result?.errors?.[0]?.extensions?.validationErrors
  ) {
    error?.networkError?.result?.errors.forEach((e: any) =>
      Object.assign(messages, Object.values(e.extensions?.validationErrors)),
    );
  } else if (error?.networkError?.result?.errors?.length) {
    messages.push(
      ...error.networkError.result.errors.map((e: any) => e.message),
    );
  }

  if (!messages.length && error?.message) {
    messages.push(error.message);
  }

  return messages;
};

export const extractGraphQlValidationDetailMessages = (error: any): string[] => {
  const messages: string[] = [];
  const errors =
    error?.graphQLErrors ?? error?.networkError?.result?.errors ?? [];

  errors.forEach((err: any) => {
    const details = err?.extensions?.details;
    if (Array.isArray(details)) {
      details.forEach((detail: Record<string, string>) => {
        Object.values(detail).forEach((message) => {
          if (typeof message === "string" && message.trim()) {
            messages.push(message);
          }
        });
      });
    }

    const validationErrors = err?.extensions?.validationErrors;
    if (validationErrors && typeof validationErrors === "object") {
      Object.values(validationErrors).forEach((message) => {
        if (Array.isArray(message)) {
          message.forEach((item) => {
            if (typeof item === "string" && item.trim()) {
              messages.push(item);
            }
          });
        } else if (typeof message === "string" && message.trim()) {
          messages.push(message);
        }
      });
    }
  });

  return messages;
};

export const ucwords = (str: string): string => {
  return str?.replace(/\b\w/g, (char) => char?.toUpperCase()) || str;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const primaryContactDetails = (contact: IContactInfo[]) => {
  return contact?.find((c: any) => c.isPrimaryContact === true);
};

export const formatDate = (dateString: any) => {
  const date = new Date(dateString);
  const formattedDate = date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  return formattedDate;
};

/**
 * @param sizes - Array of size strings to sort
 * @returns Sorted array of sizes
 */
export const sortSizes = (sizes: string[]): string[] => {
  return [...sizes].sort((a, b) => {
    const aUpper = a.toUpperCase();
    const bUpper = b.toUpperCase();

    const aOrder = SIZE_ORDER[aUpper] ?? 1000;
    const bOrder = SIZE_ORDER[bUpper] ?? 1000;

    if (aOrder !== 1000 && bOrder !== 1000) {
      return aOrder - bOrder;
    }

    if (aOrder !== 1000) return -1;
    if (bOrder !== 1000) return 1;

    return aUpper.localeCompare(bUpper);
  });
};

/**
 * Sort an object's keys (sizes) and return a new object with sorted keys
 * @param pricesObj - Object with size keys and price values
 * @returns New object with sorted size keys
 */
export const sortPricesBySizes = (pricesObj: {
  [key: string]: any;
}): { [key: string]: any } => {
  const sortedSizes = sortSizes(Object.keys(pricesObj));
  const sortedPrices: { [key: string]: any } = {};

  sortedSizes.forEach((size) => {
    sortedPrices[size] = pricesObj[size];
  });

  return sortedPrices;
};

export const getGraphQlError = (error: any) => {
  const errors: any = { networkErr: [], validationErr: [], graphQLErr: [] };
  if (error?.graphQLErrors?.length > 0) {
    error.graphQLErrors.forEach((err: any) => {
      errors.graphQLErr.push(err.message);
    });
  } else if (error?.networkError) {
    const networkResult = (error.networkError as any)?.result?.errors;
    if (networkResult?.length) {
      networkResult.forEach((err: any) => {
        const error = err?.extensions?.details ?? [];
        const detailError = typeof error[0] === "string" ? error : error[0];
        const validationErrors =
          err?.extensions?.validationErrors || detailError;
        if (validationErrors) {
          errors.validationErr.push(validationErrors);
        } else {
          errors.networkErr.push(err.message);
        }
      });
    } else if ((error?.networkError as any)?.message) {
      errors.networkErr.push((error.networkError as any).message);
    }
  }
  return errors;
};

export const BDA_SUPPLIERS_LIST_URL =
  "https://bdainc4.sharepoint.com/:x:/g/SupplierServices/IQAiGo5rs_hfRqN5woQWqzIYASYOPIh6udwcmS03_1ax3NQ?e=N38fRG";

/**
 * Returns the missing field label when the API responds with a
 * MISSING_REQUIRED_SUPPLIER_FIELD error (HTTP 422), or null otherwise.
 * Apollo Client places HTTP 4xx errors in networkError.result.errors,
 * not in graphQLErrors, so both locations are checked.
 */
export const extractMissingSupplierFieldError = (error: any): string | null => {
  const gqlError = error?.graphQLErrors?.[0];
  const networkGqlError = (error?.networkError as any)?.result?.errors?.[0];
  const anyError = gqlError ?? networkGqlError;
  if (anyError?.extensions?.code === "MISSING_REQUIRED_SUPPLIER_FIELD") {
    return anyError.extensions.missingFieldLabel ?? anyError.message ?? null;
  }
  return null;
};

export const hasProductMedia = (media: any) => {
  return (
    Array.isArray(media) &&
    media.length > 0 &&
    media.some((item) => item && item.mediaCategory === "product")
  );
};

export const handleNumericKeyDown =
  (allowDecimal: boolean) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
    ];
    if (allowedKeys.includes(e.key)) return;
    if (/^\d$/.test(e.key)) return;
    if (allowDecimal && e.key === "." && !e.currentTarget.value.includes("."))
      return;
    e.preventDefault();
  };

export const formatNumericalDates = (numericalDate: any) => {
  if (!numericalDate) return "";
  if (numericalDate === "Invalid Date") return new Date().toLocaleDateString();
  if (/^\d+$/.test(numericalDate)) {
    const date: any = new Date(Number(numericalDate));
    return !isNaN(date) ? date.toLocaleDateString() : numericalDate;
  }
  const cleanedDate = numericalDate.replace(" UTC", "Z").replace(" ", "T");
  const parsedDate: any = new Date(cleanedDate);

  if (!isNaN(parsedDate)) {
    return parsedDate.toLocaleDateString();
  }
  return numericalDate;
};

export const validatePublicOfferingUrl = (input: string) => {
  let url = input.trim();
  if (!url) {
    return { isValid: false, error: "Offering Public URL cannot be empty" };
  }
  if (url.length > 255) {
    return {
      isValid: false,
      error:
        "The URL must be less than 255 characters. Please update to continue.",
    };
  }
  if (!/^https?:\/\//i.test(url)) {
    url = `https://${url}`;
  }
  const urlRegex =
    /^(https?:\/\/)((([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})|localhost)(:\d{1,5})?(\/[^\s]*)?$/;

  if (!urlRegex.test(url)) {
    return { isValid: false, error: "Please enter a valid URL." };
  }
  try {
    new URL(url);
  } catch {
    return { isValid: false, error: "Please enter a valid URL." };
  }
  return { isValid: true, normalizedUrl: url };
};

export const camelToTitle = (str: any) => {
  return str
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s: any) => s.toUpperCase())
    .trim();
};
